import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  TextField,
  Autocomplete,
  Box,
  Alert,
  IconButton,
  Chip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  ShoppingCart as CartIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import './MedicineSales.css';

function MedicineSales() {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const { user } = useAuth();
  const [medicines, setMedicines] = useState([]);
  const [patients, setPatients] = useState([]);

  // Cart and customer details
  const [cart, setCart] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [discount, setDiscount] = useState(0);
  const [saleDate, setSaleDate] = useState(new Date().toISOString().substring(0, 10));

  const todayStr = new Date().toISOString().substring(0, 10);
  const minDate = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().substring(0, 10);
  })();

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastSale, setLastSale] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    fetchInventory();
    fetchPatients();
  }, []);

  // Auto-select patient if patientId is provided in URL
  useEffect(() => {
    if (patientId && patients.length > 0) {
      const patient = patients.find(p => p.id === parseInt(patientId));
      if (patient) {
        setSelectedPatient(patient);
      }
    }
  }, [patientId, patients]);

  useEffect(() => {
    if (selectedPatient) {
      setCustomerName(`${selectedPatient.firstName} ${selectedPatient.lastName}`);
      setCustomerPhone(selectedPatient.phone || '');
    }
  }, [selectedPatient]);

  const fetchInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filter medicines with stock > 0
      const availableInventory = response.data.filter(item => parseFloat(item.totalStock) > 0);

      const medicinesList = availableInventory.map(item => ({
        id: item.medicine.id,
        name: item.medicine.name,
        category: item.medicine.category,
        stock: parseFloat(item.totalStock),
        price: parseFloat(item.unitPrice) || 0,
        inventoryId: item.id,
        unitsPerPackage: item.unitsPerPackage || null
      }));
      setMedicines(medicinesList);
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setError('Failed to load medicines inventory');
    }
  };

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/patients?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(response.data.data);
    } catch (err) {
      console.error('Error fetching patients:', err);
    }
  };

  const addToCart = (medicine, quantity, isLoose) => {
    if (!quantity || quantity <= 0) {
      setError('Please enter a valid quantity');
      return;
    }

    const upp = medicine.unitsPerPackage || 1;
    // For loose: check tablets available = stock * unitsPerPackage
    const maxAllowed = isLoose ? medicine.stock * upp : medicine.stock;
    if (quantity > maxAllowed) {
      const unit = isLoose ? 'tablets' : 'units';
      setError(`Only ${maxAllowed} ${unit} available for ${medicine.name}`);
      return;
    }

    // Price per unit displayed: per tablet if loose, per strip if not
    const unitPrice = isLoose ? (medicine.price / upp) : medicine.price;

    // Check if same medicine + same mode already in cart
    const existingItem = cart.find(item => item.id === medicine.id && item.isLoose === isLoose);

    if (existingItem) {
      const newQuantity = existingItem.quantity + quantity;
      const newMaxAllowed = isLoose ? medicine.stock * upp : medicine.stock;
      if (newQuantity > newMaxAllowed) {
        const unit = isLoose ? 'tablets' : 'units';
        setError(`Total quantity cannot exceed ${newMaxAllowed} ${unit}`);
        return;
      }
      setCart(cart.map(item =>
        (item.id === medicine.id && item.isLoose === isLoose)
          ? { ...item, quantity: newQuantity, total: parseFloat((newQuantity * item.price).toFixed(2)) }
          : item
      ));
    } else {
      setCart([...cart, {
        id: medicine.id,
        medicineId: medicine.id,
        medicineName: medicine.name,
        category: medicine.category,
        quantity,
        price: parseFloat(unitPrice.toFixed(4)),
        total: parseFloat((quantity * unitPrice).toFixed(2)),
        maxStock: medicine.stock,
        unitsPerPackage: upp,
        isLoose: !!isLoose
      }]);
    }

    setError('');
  };

  const updateCartQuantity = (medicineId, isLoose, newQuantity) => {
    const item = cart.find(i => i.id === medicineId && i.isLoose === isLoose);
    if (!item) return;

    const maxAllowed = isLoose ? item.maxStock * item.unitsPerPackage : item.maxStock;

    if (newQuantity > maxAllowed) {
      setError(`Cannot exceed available stock of ${maxAllowed} ${isLoose ? 'tablets' : 'units'}`);
      return;
    }

    if (newQuantity <= 0) {
      removeFromCart(medicineId, isLoose);
      return;
    }

    setCart(cart.map(i =>
      (i.id === medicineId && i.isLoose === isLoose)
        ? { ...i, quantity: newQuantity, total: parseFloat((newQuantity * i.price).toFixed(2)) }
        : i
    ));
    setError('');
  };

  const removeFromCart = (medicineId, isLoose) => {
    setCart(cart.filter(item => !(item.id === medicineId && item.isLoose === isLoose)));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateDiscount = () => {
    return (calculateSubtotal() * discount) / 100;
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount();
  };

  const handleCompleteSale = async () => {
    if (cart.length === 0) {
      setError('Cart is empty');
      return;
    }

    if (!customerName.trim()) {
      setError('Customer name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      // Determine patientId: use selectedPatient.id if available, otherwise try to parse from URL
      let finalPatientId = selectedPatient?.id || null;
      if (!finalPatientId && patientId) {
        finalPatientId = parseInt(patientId);
      }

      const total = calculateTotal();
      const saleData = {
        patientId: finalPatientId,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim() || null,
        items: cart,
        subtotal: calculateSubtotal(),
        discount: calculateDiscount(),
        discountPercentage: discount,
        tax: 0,
        totalAmount: total,
        paymentStatus: 'Pending',
        paidAmount: 0,
        soldBy: user?.name || 'Unknown',
        saleDate: saleDate ? new Date(saleDate + 'T12:00:00') : new Date()
      };

      const response = await axios.post(`${API_URL}/sales`, saleData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Sale completed successfully!');
      setLastSale(response.data);

      // Reset form
      setCart([]);
      setDiscount(0);
      setSaleDate(new Date().toISOString().substring(0, 10));

      // Only reset customer details if NOT in patient mode
      if (!patientId) {
        setCustomerName('');
        setCustomerPhone('');
        setSelectedPatient(null);
      }

      // Refresh inventory
      fetchInventory();

      // Auto-open print dialog after 500ms
      setTimeout(() => {
        handlePrint(response.data);
      }, 500);
    } catch (err) {
      console.error('Error completing sale:', err);
      setError(err.response?.data?.error || 'Failed to complete sale');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (sale) => {
    const saleData = sale || lastSale;
    if (!saleData) return;

    let patientAddress = '';
    if (selectedPatient && selectedPatient.id === saleData.patientId) {
      patientAddress = [selectedPatient.street, selectedPatient.city, selectedPatient.state, selectedPatient.zipCode].filter(Boolean).join(', ');
    } else if (saleData.patientId) {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/patients/${saleData.patientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const p = res.data.data;
        if (p) {
          patientAddress = [p.street, p.city, p.state, p.zipCode].filter(Boolean).join(', ');
        }
      } catch (err) {
        console.error('Error fetching patient details for printing:', err);
      }
    }

    const ITEMS_PER_PAGE = 12;
    const CSS = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      @page { size: A5; margin: 8mm; }
      body { font-family: 'Arial', sans-serif; font-size: 9pt; }
      .bill { border: 1px solid #000; padding: 5mm; page-break-after: always; break-after: page; }
      .bill.last-page { page-break-after: auto; break-after: auto; }
      .header { border-bottom: 1px dashed #000; padding-bottom: 3mm; margin-bottom: 3mm; }
      .header-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 2mm; }
      .header-logo { width: 22mm; height: auto; object-fit: contain; }
      .header-center { flex: 1; text-align: center; padding: 0 2mm; }
      .header-center h1 { font-size: 12pt; margin-bottom: 1mm; font-weight: bold; line-height: 1.2; }
      .header-center h2 { font-size: 8pt; margin-bottom: 1mm; font-weight: 600; line-height: 1.2; }
      .header-center .address { font-size: 7pt; line-height: 1.3; margin: 0; }
      .bill-details { text-align: center; margin-top: 2mm; }
      .bill-details p { font-size: 8pt; margin: 0.5mm 0; line-height: 1.2; }
      .page-num { text-align: right; font-size: 7pt; color: #666; margin-bottom: 1mm; }
      .section { margin: 2mm 0; }
      .section-title { font-weight: bold; font-size: 9pt; border-bottom: 1px solid #000; margin-bottom: 2mm; padding-bottom: 1mm; }
      .row { display: flex; justify-content: space-between; margin: 1mm 0; font-size: 8pt; line-height: 1.3; }
      .info-row { justify-content: flex-start; gap: 2mm; }
      .info-row span:first-child { font-weight: bold; }
      .patient-info-container { display: flex; justify-content: space-between; align-items: flex-start; }
      .patient-info-right { text-align: left; max-width: 50%; font-size: 8pt; line-height: 1.3; }
      .items-table { width: 100%; margin: 2mm 0; border-collapse: collapse; }
      .items-table th { text-align: left; border-bottom: 1px solid #000; padding: 1.5mm 0; font-size: 8pt; font-weight: bold; }
      .items-table td { padding: 1mm 0; font-size: 8pt; line-height: 1.2; }
      .item-name { width: 35%; }
      .item-qty { width: 10%; text-align: center; }
      .item-price { width: 15%; text-align: right; }
      .item-gst { width: 10%; text-align: right; font-size: 7pt; }
      .item-total { width: 15%; text-align: right; }
      .item-amt { width: 15%; text-align: right; }
      .totals { border-top: 1px dashed #000; padding-top: 2mm; margin-top: 2mm; }
      .totals .row { font-size: 9pt; margin: 1mm 0; }
      .totals .final-total { font-size: 11pt; font-weight: bold; border-top: 1px solid #000; padding-top: 1.5mm; margin-top: 1.5mm; }
      .footer { text-align: center; border-top: 1px dashed #000; padding-top: 2mm; margin-top: 3mm; font-size: 7pt; line-height: 1.3; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .no-print { display: none; } }
      @media screen { body { max-width: 148mm; margin: 0 auto; background: #f0f0f0; padding: 10mm; } }`;

    const headerHTML = `
      <div class="header">
        <div class="header-top">
          <img src="/logo.jpg" alt="Logo" class="header-logo" />
          <div class="header-center">
            <h1>GLOW with SuperDoc</h1>
            <h2>MSG Hotels &amp; Resorts</h2>
            <p class="address">Opp. MSG International School</p>
            <p class="address">Nezia Khera, Sirsa</p>
            <p class="address">Haryana, India</p>
            <p class="address">Pin Code 125055</p>
            <p class="address">GST NO: 06AAJCA4867D4ZL</p>
          </div>
          <img src="/logo2.png" alt="Logo" class="header-logo" />
        </div>
        <div class="bill-details">
          <p><strong>Medicine Sales Receipt</strong></p>
          <p>Bill No: ${saleData.billNumber}</p>
          <p>Date: ${new Date(saleData.saleDate).toLocaleDateString()} ${new Date(saleData.createdAt || saleData.saleDate || new Date()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</p>
        </div>
      </div>`;

    const patientHTML = `
      <div class="section">
        <div class="section-title">Customer Details</div>
        <div class="patient-info-container">
          <div>
            <div class="row info-row"><span>Name:</span><span>${saleData.customerName}</span></div>
            ${saleData.customerPhone ? `<div class="row info-row"><span>Phone:</span><span>${saleData.customerPhone}</span></div>` : ''}
          </div>
          ${patientAddress ? `<div class="patient-info-right"><strong>Address:</strong> ${patientAddress}</div>` : ''}
        </div>
      </div>`;

    const itemsHeaderHTML = `<tr>
      <th class="item-name">Medicine</th>
      <th class="item-qty">Qty</th>
      <th class="item-price">Rate</th>
      <th class="item-total">Amount</th>
      <th class="item-gst">GST<br/>(5%)</th>
      <th class="item-amt">Total</th>
    </tr>`;

    let itemsList = [];
    if (saleData.items) {
      if (Array.isArray(saleData.items)) {
        itemsList = saleData.items;
      } else if (typeof saleData.items === 'string') {
        try {
          const parsed = JSON.parse(saleData.items);
          if (Array.isArray(parsed)) {
            itemsList = parsed;
          } else if (typeof parsed === 'string') {
            const doubleParsed = JSON.parse(parsed);
            if (Array.isArray(doubleParsed)) {
              itemsList = doubleParsed;
            }
          }
        } catch (e) {
          console.error('Error parsing items:', e);
        }
      }
    }

    const itemRows = itemsList.map(item => {
      const itemTotal = Math.round(parseFloat(item.total));
      const amtBeforeGST = Math.round(itemTotal / 1.05);
      const gstAmt = Math.round(itemTotal - amtBeforeGST);
      const priceBeforeGST = Math.round(parseFloat(item.price) / 1.05);
      const unitLabel = item.isLoose ? 'tablet' : 'strip';
      return `<tr>
        <td class="item-name">${item.medicineName}${item.isLoose ? ' <em style="font-size:7pt">(loose)</em>' : ''}</td>
        <td class="item-qty">${item.quantity} ${unitLabel}${item.quantity !== 1 ? 's' : ''}</td>
        <td class="item-price">₹${priceBeforeGST.toLocaleString('en-IN')}</td>
        <td class="item-total">₹${amtBeforeGST.toLocaleString('en-IN')}</td>
        <td class="item-gst">₹${gstAmt.toLocaleString('en-IN')}</td>
        <td class="item-amt">₹${itemTotal.toLocaleString('en-IN')}</td>
      </tr>`;
    });

    const taxable = Math.round(parseFloat(saleData.totalAmount) - parseFloat(saleData.discount || 0));
    const subtotalBeforeGST = Math.round(taxable / 1.05);
    const totalGST = taxable - subtotalBeforeGST;
    const halfGST = Math.round(totalGST / 2);

    const totalsHTML = `
      <div class="totals">
        <div class="row"><span>Subtotal (Before GST):</span><span>₹${subtotalBeforeGST.toLocaleString('en-IN')}</span></div>
        ${saleData.discount > 0 ? `<div class="row"><span>Discount (${saleData.discountPercentage}%):</span><span>- ₹${Math.round(parseFloat(saleData.discount)).toLocaleString('en-IN')}</span></div>` : ''}
        <div class="row"><span>CGST (2.5%):</span><span>₹${halfGST.toLocaleString('en-IN')}</span></div>
        <div class="row"><span>SGST (2.5%):</span><span>₹${(totalGST - halfGST).toLocaleString('en-IN')}</span></div>
        <div class="row"><span>Total GST (5%):</span><span>₹${totalGST.toLocaleString('en-IN')}</span></div>
        <div class="row final-total"><span>Total Amount:</span><span>₹${Math.round(parseFloat(saleData.totalAmount)).toLocaleString('en-IN')}</span></div>
        <div class="row" style="color:#e65100"><span>Payment Status:</span><span>Pending — Please pay at billing</span></div>
      </div>`;

    const servedByHTML = `
      <div class="section">
        <div class="row"><span>Served By:</span><span>${saleData.soldBy}</span></div>
      </div>`;

    // Split items into pages
    const chunks = [];
    for (let i = 0; i < itemRows.length; i += ITEMS_PER_PAGE) {
      chunks.push(itemRows.slice(i, i + ITEMS_PER_PAGE));
    }
    if (chunks.length === 0) chunks.push([]);
    const totalPages = chunks.length;

    const pagesHTML = chunks.map((chunk, pageIdx) => {
      const isLast = pageIdx === totalPages - 1;
      return `
        <div class="bill${isLast ? ' last-page' : ''}">
          ${headerHTML}
          ${patientHTML}
          ${totalPages > 1 ? `<div class="page-num">Page ${pageIdx + 1} of ${totalPages}</div>` : ''}
          <div class="section">
            <div class="section-title">Items</div>
            <table class="items-table">
              <thead>${itemsHeaderHTML}</thead>
              <tbody>${chunk.join('')}</tbody>
            </table>
          </div>
          ${isLast ? totalsHTML + servedByHTML : ''}
          <div class="footer">
            ${isLast
          ? '<p>Thank you for your purchase!</p><p>Please keep this bill for your records</p>'
          : `<p style="font-style:italic">Continued on next page (${pageIdx + 1}/${totalPages})</p>`}
          </div>
        </div>`;
    }).join('');

    const printContent = `<!DOCTYPE html>
      <html>
      <head>
        <title>Bill - ${saleData.billNumber}</title>
        <style>${CSS}</style>
      </head>
      <body>
        ${pagesHTML}
        <div class="no-print" style="text-align: center; margin-top: 20px;">
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">Print Bill</button>
          <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; margin-left: 10px;">Close</button>
        </div>
      </body>
      </html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CartIcon /> Medicine Sales
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          alignItems: 'start',
          width: '100%'
        }}
      >
        {/* Left Side - Medicine Selection (Half space) */}
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Select Medicines</Typography>

            <MedicineSelector
              medicines={medicines}
              onAddToCart={addToCart}
            />

            <Divider sx={{ my: 3 }} />

            {/* Available Medicines List */}
            <Typography variant="subtitle1" gutterBottom>Available Medicines</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Medicine</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell align="center"><strong>Stock</strong></TableCell>
                    <TableCell align="right"><strong>Price (₹)</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {medicines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No medicines available in stock</TableCell>
                    </TableRow>
                  ) : (
                    medicines.map((medicine) => (
                      <TableRow key={medicine.id}>
                        <TableCell>
                          {medicine.name}
                          {medicine.unitsPerPackage > 1 && (
                            <Chip label={`${medicine.unitsPerPackage}/strip`} size="small"
                              color="info" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                          )}
                        </TableCell>
                        <TableCell>{medicine.category || '-'}</TableCell>
                        <TableCell align="center">
                          <Box>
                            <Chip
                              label={`${medicine.stock} strips`}
                              size="small"
                              color={medicine.stock < 10 ? 'error' : 'success'}
                            />
                            {medicine.unitsPerPackage > 1 && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {Math.floor(medicine.stock * medicine.unitsPerPackage)} tablets
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          ₹{parseFloat(medicine.price).toFixed(2)}/strip
                          {medicine.unitsPerPackage > 1 && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              ₹{(medicine.price / medicine.unitsPerPackage).toFixed(2)}/tablet
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* Right Side - Cart and Checkout (Half space) */}
        <Box sx={{ minWidth: 0, width: '100%', position: { md: 'sticky' }, top: 20 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Cart ({cart.length} items)</Typography>

            {/* Cart Items */}
            {cart.length === 0 ? (
              <Alert severity="info" sx={{ my: 2 }}>Cart is empty</Alert>
            ) : (
              <Box sx={{ mb: 2, maxHeight: 300, overflowY: 'auto' }}>
                {cart.map((item) => {
                  const unitLabel = item.isLoose ? 'tablet' : 'strip';
                  const maxQty = item.isLoose ? item.maxStock * item.unitsPerPackage : item.maxStock;
                  return (
                    <Box key={`${item.id}-${item.isLoose}`} sx={{ p: 1, mb: 1, border: '1px solid #ddd', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {item.medicineName}
                          </Typography>
                          {item.isLoose && (
                            <Chip label="Loose" size="small" color="info" sx={{ mt: 0.3, height: 18, fontSize: '0.7rem' }} />
                          )}
                        </Box>
                        <IconButton size="small" color="error" onClick={() => removeFromCart(item.id, item.isLoose)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onChange={(e) => updateCartQuantity(item.id, item.isLoose, parseInt(e.target.value) || 0)}
                            inputProps={{ min: 1, max: maxQty }}
                            sx={{ width: 75 }}
                          />
                          <Typography variant="caption" color="text.secondary">{unitLabel}s</Typography>
                        </Box>
                        <Typography variant="body2">
                          ₹{item.price.toFixed(2)}/{unitLabel} = <strong>₹{item.total.toFixed(2)}</strong>
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Customer Details */}
            <Typography variant="subtitle1" gutterBottom>Customer Details</Typography>

            {/* Only show patient selector if NOT accessed from patient page */}
            {!patientId && (
              <Autocomplete
                options={patients}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName} - ${option.patientId}`}
                value={selectedPatient}
                onChange={(_, newValue) => setSelectedPatient(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Patient (Optional)" size="small" margin="dense" />
                )}
                fullWidth
              />
            )}

            {/* Show patient info if accessed from patient page */}
            {patientId && selectedPatient && (
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>Patient:</strong> {selectedPatient.firstName} {selectedPatient.lastName} ({selectedPatient.patientId})
                </Typography>
              </Alert>
            )}

            <TextField
              fullWidth
              label="Customer Name *"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              size="small"
              margin="dense"
              required
            />

            <TextField
              fullWidth
              label="Phone Number"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              size="small"
              margin="dense"
            />

            <TextField
              fullWidth
              type="date"
              label="Sale Date"
              value={saleDate}
              onChange={(e) => setSaleDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minDate, max: todayStr }}
              size="small"
              margin="dense"
            />

            <Divider sx={{ my: 2 }} />

            <TextField
              fullWidth
              type="number"
              label="Discount (%)"
              value={discount}
              onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              size="small"
              margin="dense"
            />

            <Box sx={{ my: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">₹{calculateSubtotal().toFixed(2)}</Typography>
              </Box>
              {discount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Discount ({discount}%):</Typography>
                  <Typography variant="body2" color="error">- ₹{calculateDiscount().toFixed(2)}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.1rem', borderTop: '2px solid #ddd', pt: 1 }}>
                <Typography variant="body1"><strong>Total:</strong></Typography>
                <Typography variant="body1"><strong>₹{calculateTotal().toFixed(2)}</strong></Typography>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 1, mb: 1 }} icon={false}>
              <Typography variant="caption">
                Payment will be recorded via <strong>Billing</strong> module.
              </Typography>
            </Alert>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              onClick={handleCompleteSale}
              disabled={loading || cart.length === 0}
              sx={{ mt: 2 }}
            >
              {loading ? 'Processing...' : 'Complete Sale'}
            </Button>

            {lastSale && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => handlePrint(lastSale)}
                sx={{ mt: 1 }}
              >
                Print Last Bill
              </Button>
            )}
          </Paper>
        </Box>
      </Box>
    </Container>
  );
}

// Medicine Selector Component
function MedicineSelector({ medicines, onAddToCart }) {
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoose, setIsLoose] = useState(false);

  const canSellLoose = selectedMedicine?.unitsPerPackage > 1;
  const upp = selectedMedicine?.unitsPerPackage || 1;
  const maxQty = selectedMedicine
    ? (isLoose ? Math.floor(selectedMedicine.stock * upp) : Math.floor(selectedMedicine.stock))
    : 1;
  const unitLabel = isLoose ? 'tablet' : 'strip';
  const pricePerUnit = selectedMedicine
    ? (isLoose ? selectedMedicine.price / upp : selectedMedicine.price)
    : 0;

  const handleMedicineChange = (newValue) => {
    setSelectedMedicine(newValue);
    setIsLoose(false);
    setQuantity(1);
  };

  const handleAdd = () => {
    if (selectedMedicine) {
      onAddToCart(selectedMedicine, quantity, isLoose);
      setSelectedMedicine(null);
      setQuantity(1);
      setIsLoose(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Autocomplete
          options={medicines}
          getOptionLabel={(option) => `${option.name}${option.unitsPerPackage > 1 ? ` (${option.unitsPerPackage}/strip)` : ''} — Stock: ${option.stock} strips`}
          value={selectedMedicine}
          onChange={(_, newValue) => handleMedicineChange(newValue)}
          renderInput={(params) => <TextField {...params} label="Search Medicine" />}
          sx={{ flex: 1 }}
          size="small"
        />
        <TextField
          type="number"
          label={`Qty (${unitLabel}s)`}
          value={quantity}
          onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
          inputProps={{ min: 1, max: maxQty }}
          sx={{ width: 120 }}
          size="small"
          helperText={selectedMedicine ? `Max: ${maxQty}` : ''}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!selectedMedicine}
          size="medium"
          sx={{ mt: 0.3 }}
        >
          Add
        </Button>
      </Box>

      {/* Loose selling toggle — only shown when medicine supports it */}
      {canSellLoose && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 0.5 }}>
          <Box sx={{ display: 'flex', border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
            <Button
              size="small"
              variant={!isLoose ? 'contained' : 'text'}
              onClick={() => { setIsLoose(false); setQuantity(1); }}
              sx={{ borderRadius: 0, px: 2, py: 0.5, minWidth: 80 }}
            >
              By Strip
            </Button>
            <Button
              size="small"
              variant={isLoose ? 'contained' : 'text'}
              color={isLoose ? 'info' : 'inherit'}
              onClick={() => { setIsLoose(true); setQuantity(1); }}
              sx={{ borderRadius: 0, px: 2, py: 0.5, minWidth: 80 }}
            >
              Loose
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {isLoose
              ? `₹${pricePerUnit.toFixed(2)}/tablet × ${quantity} = ₹${(pricePerUnit * quantity).toFixed(2)}`
              : `₹${pricePerUnit.toFixed(2)}/strip × ${quantity} = ₹${(pricePerUnit * quantity).toFixed(2)}`}
          </Typography>
        </Box>
      )}
      {!canSellLoose && selectedMedicine && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
          ₹{pricePerUnit.toFixed(2)}/strip × {quantity} = ₹{(pricePerUnit * quantity).toFixed(2)}
        </Typography>
      )}
    </Box>
  );
}

export default MedicineSales;